/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import config from "../../../../share/config";
import util from "../../../../share/util/utils";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  COMPLETED: "completed",
  ERROR: "error",
  COMMON_ERROR: "commonError"
};

export default {
  name: "requirement113",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      waitTime: 0,
      showRegisteredDay: null,
      resDataId: null,
      modal_requirement_113: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.COMMON_ERROR:
          return "danger";
        case PHASE.COMPLETED:
          return "success";
        case PHASE.START:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_113 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_113 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー表示
      this.phase = PHASE.COMMON_ERROR;
    },

    /** 固有エラー表示 */
    async readError() {
      this.phase = PHASE.ERROR;
      // ウェイト時間待機してウィザードを閉じる
      await util.sleep(this.waitTime);
      this.closeWizard();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** バリアントコード読出し */
    async onRead() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: "read"
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 完了画面に遷移
              this.showRegisteredDay = util.timeConverter(response.data.user_data.data_id);
              this.resDataId = response.data.user_data.data_id;
              this.phase = PHASE.COMPLETED;
              break;
            case config.STATUS_WSP_FAILED:
              this.waitTime = response.data.wait_time;
              // 異常終了
              this.readError();
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    onOkClicked() {
      this.closeWizard();
    }
  },

  async mounted() {
    this.init();
    // バリアントコード読出し
    this.onRead();
  }
};
